import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CoffeeShop,
    adjustedGoogleRating as computeAdjustedGoogle,
    interpolateRatingColor,
    getCoffeeIconUrl
} from '../hooks/useCoffeeShops';
import {
    formatStamp,
    lastRatedAt,
    priceMarks,
    shopTypeTags,
    topDrinkOf
} from '../utils/shopDerive';
import { formatDistance } from '../hooks/useUserLocation';

interface ShopCardProps {
    shop: CoffeeShop;
    coffeeTypesCache?: Record<string, { iconName: string; displayName: string }>;
    distanceKm?: number;
    /** when set, show Google rating pill (e.g. from recommend API); otherwise derived from shop.googleRating + googleReviewCount */
    adjustedGoogleRating?: number;
    /** legacy 'row' layout still supported for callers; default 'grid' is the redesigned card */
    layout?: 'grid' | 'row';
    onClick?: () => void;
}

const ShopCard: React.FC<ShopCardProps> = ({
    shop,
    coffeeTypesCache = {},
    distanceKm,
    adjustedGoogleRating: adjustedGoogleProp,
    layout = 'grid',
    onClick
}) => {
    const navigate = useNavigate();
    // Only show Google chip when there's real signal — at least 5 reviews. The
    // Bayesian-adjusted formula collapses to the prior (≈3) when reviewCount is 0,
    // which produces a misleading "G 3.0" stamp on shops Google has never rated.
    const hasGoogleSignal = (shop.googleReviewCount ?? 0) >= 5 && (shop.googleRating ?? 0) > 0;
    const adjustedGoogle =
        adjustedGoogleProp ??
        (hasGoogleSignal
            ? computeAdjustedGoogle(shop.googleRating, shop.googleReviewCount)
            : undefined);

    const handleClick = () => {
        if (onClick) onClick();
        else navigate(`/coffeeshops/${shop._id}`);
    };

    const top = topDrinkOf(shop, coffeeTypesCache);
    const stamp = formatStamp(lastRatedAt(shop));
    const tags = shopTypeTags(shop);
    const price = priceMarks(shop.priceLevel);
    const ratingColor = interpolateRatingColor(shop.averageRating);

    const drinksCount = Object.keys(shop.coffeeTypeStats || {}).length;

    if (layout === 'row') {
        return <ShopCardRow
            shop={shop}
            top={top}
            stamp={stamp}
            distanceKm={distanceKm}
            adjustedGoogle={adjustedGoogle}
            ratingColor={ratingColor}
            onClick={handleClick}
        />;
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            className="group relative text-left bg-paper border-brutal border-ink shadow-brutal-sm rounded-sm p-4 sm:p-5 flex flex-col gap-3 hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-sm transition-all"
        >
            {/* date stamp ribbon (top-left) */}
            {stamp && (
                <span className="absolute -top-px -left-px bg-ink text-paper text-[9px] font-black uppercase tracking-[0.12em] px-2 py-0.5">
                    {stamp}
                </span>
            )}

            {/* header: name + meta on left, score on right */}
            <div className="flex items-start gap-3 mt-2">
                <div className="flex-1 min-w-0">
                    {tags.length > 0 && (
                        <div className="text-[9px] font-black uppercase tracking-[0.14em] text-ink/55 mb-1">
                            ◉ {tags.join(' · ')}
                        </div>
                    )}
                    <h3 className="font-black text-ink text-base sm:text-lg leading-tight tracking-tight truncate">
                        {shop.name}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-ink/65 mt-1">
                        <span className="truncate">{shop.city}, {shop.country}</span>
                        {price && (
                            <>
                                <span className="text-ink/30">·</span>
                                <span className="font-black tracking-wider text-ink/65">
                                    <span>{price.filled}</span>
                                    <span className="text-ink/25">{price.faded}</span>
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div
                        className="text-2xl font-black leading-none tabular-nums"
                        style={{ color: ratingColor }}
                    >
                        {shop.averageRating.toFixed(1)}
                        <span className="text-xs text-ink/40 font-bold ml-0.5">/10</span>
                    </div>
                    {adjustedGoogle != null && adjustedGoogle > 0 && (
                        <span className="inline-block mt-1 bg-ink text-paper text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5">
                            G {adjustedGoogle.toFixed(1)}
                        </span>
                    )}
                </div>
            </div>

            {/* best drink callout — coffee-brown left border for warmth */}
            {top ? (
                <div
                    className="flex items-center gap-3 px-3 py-2 bg-ink/5 border-l-4"
                    style={{ borderLeftColor: '#7a4a2e' }}
                >
                    <img
                        src={getCoffeeIconUrl(top.iconName)}
                        alt=""
                        className="w-8 h-8 object-contain shrink-0"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = getCoffeeIconUrl('espresso');
                        }}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-black uppercase tracking-[0.18em] text-ink/55">
                            BEST RATED
                        </div>
                        <div className="text-sm font-bold text-ink truncate">{top.displayName}</div>
                    </div>
                    <div className="text-base font-black tabular-nums text-ink shrink-0">
                        {top.averageRating.toFixed(1)}
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-3 px-3 py-2 bg-ink/5 border-l-4 border-ink/15">
                    <div className="text-[9px] font-black uppercase tracking-[0.18em] text-ink/45">
                        No drinks rated yet
                    </div>
                </div>
            )}

            {/* footer: ratings + drinks + distance */}
            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.10em] text-ink/55 border-t border-dashed border-ink/20 pt-2">
                <span>
                    {shop.totalRatings} {shop.totalRatings === 1 ? 'rating' : 'ratings'}
                    {drinksCount > 0 && ` · ${drinksCount} ${drinksCount === 1 ? 'drink' : 'drinks'}`}
                </span>
                {typeof distanceKm === 'number' && (
                    <span className="inline-flex items-center gap-1 normal-case tracking-wider">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-status-completed" />
                        {formatDistance(distanceKm)}
                    </span>
                )}
            </div>
        </button>
    );
};

// ---------- compact row variant (kept for callers passing layout="row") ----------
interface RowProps {
    shop: CoffeeShop;
    top: ReturnType<typeof topDrinkOf>;
    stamp: string | null;
    distanceKm?: number;
    adjustedGoogle?: number;
    ratingColor: string;
    onClick: () => void;
}

const ShopCardRow: React.FC<RowProps> = ({
    shop, top, stamp, distanceKm, adjustedGoogle, ratingColor, onClick
}) => (
    <button
        type="button"
        onClick={onClick}
        className="group w-full text-left py-3 px-2 flex items-center gap-3 border-b border-ink/15 hover:bg-ink/[0.03] transition-colors"
    >
        <div className="shrink-0 w-12 text-right">
            <div
                className="text-2xl font-black leading-none tabular-nums"
                style={{ color: ratingColor }}
            >
                {shop.averageRating.toFixed(1)}
            </div>
            <div className="text-[9px] font-black uppercase tracking-widest text-ink/50 mt-0.5">
                {shop.totalRatings} R.
            </div>
        </div>
        <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
                <h3 className="font-black text-ink text-base leading-tight truncate">
                    {shop.name}
                </h3>
                {adjustedGoogle != null && adjustedGoogle > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-ink/55">
                        G {adjustedGoogle.toFixed(1)}
                    </span>
                )}
            </div>
            <div className="text-xs text-ink/65 truncate">
                {shop.city}, {shop.country}
                {top && ` · ${top.displayName}`}
                {typeof distanceKm === 'number' && ` · ${formatDistance(distanceKm)}`}
            </div>
            {stamp && (
                <div className="text-[9px] font-black uppercase tracking-widest text-ink/40 mt-0.5">
                    {stamp}
                </div>
            )}
        </div>
        <svg
            className="w-4 h-4 text-ink/30 group-hover:text-ink/60 flex-shrink-0 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    </button>
);

export default ShopCard;
